/**
 * DocumentService handles saving and versioning of documents/snippets in the editor.
 * It manages the interaction between the YJS document state, content updates, and
 * server persistence while handling error states and user notifications.
 */

import { get } from "svelte/store";
import type { UpdateContent, YdocStore } from "$components/editor";
import { DocumentTypeEnum } from "app/models/documents";
import type { Writable } from "svelte/store";

// custom error for empty document state
export class JsonEmpty extends Error {}

// configuration required to initialize the service
export interface SaveSpaceConfig {
  ydoc: YdocStore;
  contentUpdate: UpdateContent;
  onSaveDoc: (
    json: any,
    text: string,
    ydoc: Uint8Array,
    title: string,
    isSavingDraft: boolean
  ) => Promise<{ json: any } | undefined>;
  user: any;
  docType: DocumentTypeEnum;
  versionHistoryFlag: boolean;
  versionHistoryModalOpen: Writable<boolean>;
  alerts: any;
}

export class DocumentService {
  private config: SaveSpaceConfig;

  constructor(config: SaveSpaceConfig) {
    this.config = config;
  }

  // main public method to save document changes
  // handles both drafts and published versions with optional comments
  async saveSpace(isDraft = false, comment = "") {
    const { ydoc, user, docType, versionHistoryFlag, versionHistoryModalOpen, alerts } =
      this.config;

    // early return if user not authenticated or no ydoc
    if (!user.isAuthed || !get(ydoc)) return;

    let version;
    // create version history entry for non-draft documents and snippets
    if (
      !isDraft &&
      (docType === DocumentTypeEnum.Document || docType === DocumentTypeEnum.Snippet)
    ) {
      versionHistoryModalOpen.set(false);
      version = ydoc.createVersion(comment, {
        handle: user.handle,
        profileImage: user.profileImage,
        uuid: user.uuid,
      });
    }

    try {
      // attempt to save and show appropriate success messages
      const update = await this._saveSpace(isDraft);
      if (update) {
        if (versionHistoryFlag && version) {
          alerts.success("New version created", {
            description: "View all versions via the doc version history",
          });
        } else if (version) {
          alerts.success("Document updated");
        }
      }
      return update;
    } catch (e: any) {
      this.handleSaveError(e, version, isDraft);
    }
  }

  // internal method to handle the actual save operation
  // validates document state before saving
  private async _saveSpace(isDraft: boolean) {
    const { contentUpdate, docType, ydoc, onSaveDoc } = this.config;

    // validation checks
    if (!contentUpdate) {
      console.error("Content update is undefined");
      throw new Error("Content update is missing.");
    }

    // ensure title exists for documents and snippets
    if (
      (docType === DocumentTypeEnum.Document || docType === DocumentTypeEnum.Snippet) &&
      contentUpdate.title === ""
    ) {
      throw new Error("Please add a title before submitting.");
    }

    // ensure ydoc is properly initialized
    if (!get(ydoc)?.doc) {
      console.error("Ydoc is undefined or missing doc");
      throw new Error("Ydoc is not properly initialized.");
    }

    // check for empty content
    if (contentUpdate.empty) {
      throw new JsonEmpty();
    }

    // attempt to save via provided callback
    try {
      return await onSaveDoc(
        contentUpdate.json,
        contentUpdate.markdown || "",
        ydoc.serialise(),
        contentUpdate.title,
        isDraft
      );
    } catch (error) {
      console.error("Error during onSaveDoc:", error);
      throw error;
    }
  }

  // handles various error cases during save operation
  // shows appropriate error messages to user
  private handleSaveError(e: any, version: any, isDraft: boolean) {
    const { alerts, ydoc, versionHistoryFlag } = this.config;
    let message = "Failed to update changes";

    // cleanup version if save failed
    if (version) {
      ydoc.removeVersion();
    }

    // handle empty document case
    if (e instanceof JsonEmpty) {
      alerts.error("Please add content before submitting.");
      return;
    } else if (versionHistoryFlag && version) {
      message = "Failed to create new version";
    }

    // show error messages for non-draft saves
    if (!isDraft) {
      if (e.message) {
        alerts.error(e.message);
      }
      alerts.error(message, {
        description:
          "Please check your internet connection and try again. Your draft is saved locally.",
      });
    }
  }
}

// factory function for easier instantiation
export function createDocumentService(config: SaveSpaceConfig): DocumentService {
  return new DocumentService(config);
}
